import { Effect, Option, Queue, Schema as S, Stream } from "effect";
import * as ManagedResource from "foldkit/managedResource";
import * as Subscription from "foldkit/subscription";

import { Message, type Model } from "../app/main";
import type { ClerkAdapter } from "./adapter";
import type { User } from "./model";

type SessionQueue = Queue.Queue<User | null>;

export const ClerkSession = ManagedResource.tag<SessionQueue>()("ClerkSession");
export type ClerkSessionService = ManagedResource.ServiceOf<
	typeof ClerkSession
>;

/** Loads Clerk and subscribes via `addListener`; emissions land in the queue, release unsubscribes. */
const acquireSession = (adapter: ClerkAdapter) =>
	Effect.gen(function* () {
		yield* Effect.promise(() => adapter.load());
		const queue = yield* Queue.unbounded<User | null>();
		yield* Effect.acquireRelease(
			Effect.sync(() =>
				adapter.addListener((session) => Queue.offerUnsafe(queue, session)),
			),
			(unsubscribe) => Effect.sync(unsubscribe),
		);
		return queue;
	});

/** One-shot stream form of the listener for adapter tests (no runtime needed). */
export const sessionStream = (adapter: ClerkAdapter) =>
	Stream.unwrap(
		acquireSession(adapter).pipe(
			Effect.map((queue) =>
				Stream.fromQueue(queue).pipe(
					Stream.map((session) => Message.SessionChanged({ session })),
				),
			),
		),
	);

export const makeSessionResources = (adapter: ClerkAdapter) =>
	ManagedResource.make<Model, Message>()((entry) => ({
		clerkSession: entry(S.Option(S.Null), {
			resource: ClerkSession,
			modelToMaybeRequirements: () => Option.some(null),
			acquire: () => acquireSession(adapter),
			release: () => Effect.void,
			onAcquired: () => Message.ClerkReady(),
			onReleased: () => Message.ClerkReleased(),
			onAcquireError: (error) =>
				Message.FailedClerkLoad({ error: String(error) }),
		}),
	}));

/** Drains the acquired queue into `SessionChanged` once the resource is ready. */
export const sessionSubscriptions = Subscription.make<
	Model,
	Message,
	ClerkSessionService
>()((entry) => ({
	clerkSession: entry(
		{ clerkReady: S.Boolean, clerkEpoch: S.Number },
		{
			modelToDependencies: (model) => ({
				clerkReady: model.clerkReady,
				clerkEpoch: model.clerkEpoch,
			}),
			dependenciesToStream: ({ clerkReady }) =>
				clerkReady
					? Stream.unwrap(
							ClerkSession.get.pipe(
								Effect.map((queue) =>
									Stream.fromQueue(queue).pipe(
										Stream.map((session) =>
											Message.SessionChanged({ session }),
										),
									),
								),
								Effect.orElseSucceed(() => Stream.empty),
							),
						)
					: Stream.empty,
		},
	),
}));
